import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieService from "../../context/cookies";

import { base } from "../../constants/apis";
import {
  TradeFlowData,
  TradeFlowJson,
  TradeFlowRes,
} from "../../types/TradeFlow";

export type UpdateTradeFlow =
  | {
      id: string | number;
      row_id: string;
      trade_flow_json: TradeFlowJson[];
      action: string;
      table_id: number;
    }
  | {
      id: string | number;
      row_id: string;
      trade_flow_json: TradeFlowJson[];
    };

export const tradeFlowApi = createApi({
  reducerPath: "tradeFlowApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis/trade_flow`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("jwtToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["tradeFlowApi", "accessControltradeFlowApi"],

  endpoints: (builder) => ({
    getTradeFlowById: builder.query<TradeFlowRes, string>({
      query: (row_id) => ({
        url: "/get.php",
        method: "POST",
        body: { row_id },
      }),

      providesTags: ["tradeFlowApi"],
    }),
    addTradeFlow: builder.mutation({
      query: (payload) => ({
        url: "/add.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["tradeFlowApi"],
    }),

    updateTradeFlow: builder.mutation<TradeFlowData, UpdateTradeFlow>({
      query: (payload) => ({
        url: "/update.php",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(
        { row_id, trade_flow_json: updatedSteps },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          tradeFlowApi.util.updateQueryData(
            "getTradeFlowById",
            row_id,
            (draft) => {
              draft.data.trade_flow_json = updatedSteps;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ["tradeFlowApi"],
    }),

    updateTradeFlowByIdAndType: builder.mutation({
      query: (payload) => ({
        url: "/type_wise_update.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["tradeFlowApi"],
    }),

    addBookingQuotes: builder.mutation({
      query: (payload) => ({
        url: "/add_quotes.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["tradeFlowApi"],
    }),

    getContracts: builder.query({
      query: (payload) => ({
        url: "/get_contacts.php",
        method: "POST",
        body: payload,
      }),
      transformResponse: (res) => res.data,
    }),

    // ------ access control -----

    getAccessControlTradeFlowById: builder.query<
      TradeFlowRes,
      { row_id: string; parent_id: number }
    >({
      query: (payload) => ({
        url: "/access_control/get.php",
        method: "POST",
        body: payload,
      }),

      providesTags: ["accessControltradeFlowApi"],
    }),

    addAccessControlTradeFlow: builder.mutation({
      query: (payload) => ({
        url: "/access_control/add.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["accessControltradeFlowApi"],
    }),

    updateAccessControlTradeFlow: builder.mutation<
      TradeFlowData,
      UpdateTradeFlow & { parent_id: number }
    >({
      query: (payload) => ({
        url: "/access_control/update.php",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(
        { row_id, parent_id, trade_flow_json: updatedSteps },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          tradeFlowApi.util.updateQueryData(
            "getAccessControlTradeFlowById",
            { row_id, parent_id },
            (draft) => {
              draft.data.trade_flow_json = updatedSteps;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ["accessControltradeFlowApi"],
    }),
  }),
});

export const {
  useAddTradeFlowMutation,
  useGetTradeFlowByIdQuery,
  useUpdateTradeFlowMutation,
  useUpdateTradeFlowByIdAndTypeMutation,
  useAddBookingQuotesMutation,
  useGetContractsQuery,

  useAddAccessControlTradeFlowMutation,
  useGetAccessControlTradeFlowByIdQuery,
  useUpdateAccessControlTradeFlowMutation,
} = tradeFlowApi;
